import { useState } from "react";
import { LeadService } from "@/services/LeadService";
import { toaster } from "@/components/ui/toaster";
import ChangeLeadStatusModal from "../components/lead/ChangeLeadStatusModal";
import ChangeLeadInterestLevelModal from "../components/lead/ChangeLeadInterestLevelModal";
import AddLeadNoteModal from "../components/lead/AddLeadNoteModal";
import AddLeadTimelineEntryModal from "../components/lead/AddLeadTimelineEntryModal";

/** Mirrors useCandidateSingleActions.js, trimmed: no email modal (no single/bulk email endpoint for Lead), no file attachments. */
const useLeadSingleActions = ({ onStatusChanged, onNoteAdded, onTimelineEntryAdded, onInterestLevelChanged } = {}) => {
    const [activeLeadId, setActiveLeadId] = useState(null);
    const [activeLeadStatus, setActiveLeadStatus] = useState(null);
    const [activeLeadInterestLevel, setActiveLeadInterestLevel] = useState(null);

    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showInterestLevelModal, setShowInterestLevelModal] = useState(false);
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [showTimelineModal, setShowTimelineModal] = useState(false);

    const [isStatusLoading, setIsStatusLoading] = useState(false);
    const [isInterestLevelLoading, setIsInterestLevelLoading] = useState(false);
    const [isNoteLoading, setIsNoteLoading] = useState(false);
    const [isTimelineLoading, setIsTimelineLoading] = useState(false);

    const [noteContent, setNoteContent] = useState("");
    const [timelineDescription, setTimelineDescription] = useState("");
    const [timelineOccurredAt, setTimelineOccurredAt] = useState("");

    const openStatusModal = (leadId, currentStatus) => {
        setActiveLeadId(leadId);
        setActiveLeadStatus(currentStatus);
        setShowStatusModal(true);
    };

    const openInterestLevelModal = (leadId, currentInterestLevel) => {
        setActiveLeadId(leadId);
        setActiveLeadInterestLevel(currentInterestLevel);
        setShowInterestLevelModal(true);
    };

    const openNoteModal = (leadId) => {
        setActiveLeadId(leadId);
        setShowNoteModal(true);
    };

    const closeNoteModal = () => {
        setShowNoteModal(false);
        setNoteContent("");
    };

    const openTimelineModal = (leadId) => {
        setActiveLeadId(leadId);
        setTimelineDescription("");
        setShowTimelineModal(true);
    };

    const closeTimelineModal = () => {
        setShowTimelineModal(false);
        setTimelineDescription("");
        setTimelineOccurredAt("");
    };

    const handleChangeStatus = async (newStatus, note) => {
        setIsStatusLoading(true);
        try {
            const updated = await LeadService.changeStatus(activeLeadId, newStatus, note);
            toaster.create({ type: "success", title: "Status zaktualizowany" });
            setShowStatusModal(false);
            onStatusChanged?.(activeLeadId, updated);
        } catch {
            toaster.create({ type: "error", title: "Nie udało się zaktualizować statusu" });
        } finally {
            setIsStatusLoading(false);
        }
    };

    const handleChangeInterestLevel = async (interestLevel) => {
        setIsInterestLevelLoading(true);
        try {
            const updated = await LeadService.setInterestLevel(activeLeadId, interestLevel);
            toaster.create({ type: "success", title: "Poziom zainteresowania zaktualizowany" });
            setShowInterestLevelModal(false);
            onInterestLevelChanged?.(activeLeadId, updated);
        } catch {
            toaster.create({ type: "error", title: "Nie udało się zaktualizować poziomu zainteresowania" });
        } finally {
            setIsInterestLevelLoading(false);
        }
    };

    const handleAddNote = async () => {
        if (!noteContent.trim()) return;
        setIsNoteLoading(true);
        try {
            const note = await LeadService.addNote(activeLeadId, noteContent.trim());
            toaster.create({ type: "success", title: "Notatka dodana" });
            closeNoteModal();
            onNoteAdded?.(activeLeadId, note);
        } catch {
            toaster.create({ type: "error", title: "Nie udało się dodać notatki" });
        } finally {
            setIsNoteLoading(false);
        }
    };

    const handleAddTimelineEntry = async () => {
        if (!timelineDescription.trim()) return;
        setIsTimelineLoading(true);
        try {
            await LeadService.addTimelineEntry(
                activeLeadId, "ManualEntry", timelineDescription.trim(), new Date(timelineOccurredAt).toISOString()
            );
            toaster.create({ type: "success", title: "Wpis dodany" });
            closeTimelineModal();
            onTimelineEntryAdded?.(activeLeadId);
        } catch {
            toaster.create({ type: "error", title: "Nie udało się dodać wpisu" });
        } finally {
            setIsTimelineLoading(false);
        }
    };

    const modals = (
        <>
            <ChangeLeadStatusModal
                isOpen={showStatusModal}
                onClose={() => setShowStatusModal(false)}
                currentStatus={activeLeadStatus}
                onSubmit={handleChangeStatus}
                isLoading={isStatusLoading}
            />
            <ChangeLeadInterestLevelModal
                isOpen={showInterestLevelModal}
                onClose={() => setShowInterestLevelModal(false)}
                currentInterestLevel={activeLeadInterestLevel}
                onSubmit={handleChangeInterestLevel}
                isLoading={isInterestLevelLoading}
            />
            <AddLeadNoteModal
                isOpen={showNoteModal}
                onClose={closeNoteModal}
                noteContent={noteContent}
                setNoteContent={setNoteContent}
                onSubmit={handleAddNote}
                isLoading={isNoteLoading}
            />
            <AddLeadTimelineEntryModal
                isOpen={showTimelineModal}
                onClose={closeTimelineModal}
                description={timelineDescription}
                setDescription={setTimelineDescription}
                occurredAt={timelineOccurredAt}
                setOccurredAt={setTimelineOccurredAt}
                onSubmit={handleAddTimelineEntry}
                isLoading={isTimelineLoading}
            />
        </>
    );

    return { openStatusModal, openInterestLevelModal, openNoteModal, closeNoteModal, openTimelineModal, modals };
};

export default useLeadSingleActions;
